import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';

import PacksList from '@/components/app/cards/PacksList';
import { PacksContext } from '@/components/app/layout/PacksProvider';
import Layout from '@/components/layout/LayoutWrapper';
import { usePacksFeed } from '@/hooks/app/usePacksFeed';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { calmLogger } from '@/utils/calmLogger';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

export const customFeedNames: Record<string, string> = {
	work: 'collection-calm-for-work',
};

const PackFeed = () => {
	const { query } = useRouter();
	const routerPush = useRouterPush();
	const feedQueryParam = typeof query?.feedName === 'string' ? query.feedName : '';

	const feedName = customFeedNames[feedQueryParam] ?? feedQueryParam;

	const { packs, tags, isLoading, title: feedTitle } = usePacksFeed(feedName);
	const { setPageTitle } = useContext(PacksContext);

	// The better solution here would be to figure out the lifecycle of the above
	// loading state to see why occasionally isLoading is false when it should be true
	// and thus prematurely redirecting but I have to move on with my life so I'm
	// using a timeout - MB
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (!isLoading && packs.length <= 0) {
				routerPush('/404').catch(error =>
					calmLogger.error('Error in app PackFeed routerPush to /404', {}, error),
				);
			}
		}, 500);

		return () => clearTimeout(timeout);
	}, [isLoading, packs, routerPush]);

	useEffect(() => {
		if (feedTitle) {
			setPageTitle(feedTitle);
		}
	}, [feedTitle, setPageTitle]);

	if (!isLoading && packs.length <= 0) {
		return null;
	}

	return (
		<Layout pageName={feedName}>
			<PacksList
				packFeedName={feedName}
				packs={packs}
				tags={tags}
				activeTag={tags[0]}
				isLoading={isLoading}
			/>
		</Layout>
	);
};

export default PackFeed;

export const getServerSideProps: GetServerSideProps = async context => {
	const initialState = await getInitialServerState(context);

	return {
		props: {
			initialState: {
				...initialState,
				theme: {
					webAppLayout: 'full',
				},
			},
		},
	};
};
